import classNames from "classnames";
import { useEffect, useState } from "react";
import { useQueryCache } from "react-query";
import { useParams } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Container, Header, Loader, Segment } from "semantic-ui-react";
import { ParsedPhoneNumber } from "../../../../../../components/global_components/form_components/ControlledPhoneNumber";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useAnonymousToken } from "../../../../../auth/hooks/useAnonymousToken";
import { useAuthIssueAnonymousTokenByOTP } from "../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTP";
import {
  buildReturnUrl,
  useAuthIssueAnonymousTokenByOTPInURL,
} from "../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { useAuthOTPSend } from "../../../../../auth/hooks/useAuthOTPSend";
import { useNotifyAppDownloadLinks } from "../../../../hooks/useNotifyAppDownloadLinks";
import { useNotifySettingsPublic } from "../../../../hooks/useNotifySettingsPublic";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useNotifySubscriptionGroupsPublic } from "../../../../hooks/useNotifySubscriptionGroupsPublic";
import { usePerformActionFromUrl } from "../../../../hooks/usePerformActionFromUrl";
import { buildIdentifierString } from "../../subscribers/utils/buildIdentifierString";
import { NotifyPublicTopNavigation } from "../components/NotifyPublicTopNavigation";
import { ManageEmailOrPhoneNumberCard } from "./components/ManageEmailOrPhoneNumberCard";
import { SubscriberContactMethodTable } from "./components/SubscriberContactMethodTable";
import { AddSubscriberContactMethodModal } from "./components/modals/AddSubscriberContactMethodModal";
import { OTPModalData, VerifyOTPModal } from "./components/modals/VerifyOTPModal";
import { AnonymousSubscriptionGroupsTable } from "./components/subscription-groups-tables/AnonymousSubscriptionGroupsTable";
import { LoggedInSubscriptionGroupsTable } from "./components/subscription-groups-tables/LoggedInSubscriptionGroupsTable";
import { PublicSubscriptionGroupsTable } from "./components/subscription-groups-tables/PublicSubscriptionGroupsTable";
import DashboardPreview from "../../../../../app-preview/pages/DashboardPreview";
import NotificationPreview from "../../../../../app-preview/pages/NotificationPreview";
import { ToastStatuses } from "../../../../../../components/global_components/Toast";

const ActionType = {
  AddContactMethod: "add-contact-method",
  LoginAnonymously: "login-anonymously",
  OpenVerifyOtpModal: "open-verify-otp-modal",
} as const;
type ActionType = (typeof ActionType)[keyof typeof ActionType];

export function PublicSubscriptionGroupsPage() {
  const { site } = useParams<{ site: string }>();
  const { addToast } = useToasts();

  const [isAddContactMethodModalOpen, setIsAddContactMethodModalOpen] = useState(false);
  const [otpModalData, setOTPModalData] = useState<OTPModalData | null>(null);

  const { login } = useAnonymousToken();

  useAuthIssueAnonymousTokenByOTPInURL();
  usePerformActionFromUrl<ActionType>({
    onAction(action, params) {
      if (action === ActionType.AddContactMethod) {
        setIsAddContactMethodModalOpen(true);
      }
      if (action === ActionType.LoginAnonymously && params?.token && params?.identifier) {
        const identifier = +params.identifier;
        login(params.token, identifier);
      }
      if (action === ActionType.OpenVerifyOtpModal && params?.identifier && params?.type) {
        if (!params.type || !["Email", "Phone"].includes(params.type)) return;
        setOTPModalData({
          identifier: params.identifier,
          type: params.type as "Email" | "Phone",
        });
      }
    },
  });

  const { issueAnonymousTokenByOTP, isAuthIssueAnonymousTokenLoading } = useAuthIssueAnonymousTokenByOTP({
    onSuccess: () => {
      setOTPModalData(null);
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: ToastStatuses.Error,
        autoDismiss: true,
      });
    },
  });

  const { sendAuthOTP, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: ({ identifier, type }) => {
      setIsAddContactMethodModalOpen(false);
      setOTPModalData({
        identifier,
        type,
      });
    },
  });

  const { isSubscriberContactMethodsLoading, isLoggedIn, isLoggedInAnonymously } =
    useNotifySubscriberContactMethodsForLoggedInUser({
      onSuccessfullyLinked: (linkedSubscriberContactMethod) =>
        addToast(`Added ${buildIdentifierString(linkedSubscriberContactMethod)}`, {
          appearance: ToastStatuses.Success,
          autoDismiss: true,
        }),
    });

  const { downloadLinks, isDownloadLinksLoading } = useNotifyAppDownloadLinks();
  const { isConfigured, isLoading, publicNotifySettings } = useNotifySettingsPublic();
  const { isPublicSubscriptionGroupsLoading } = useNotifySubscriptionGroupsPublic();

  const devicePreviewIsLoaded = useQueryHasData(["citizen.site", site]);
  const delayedIsDevicePreviewLoading = useDebounce(!devicePreviewIsLoaded, 250);

  const isPageLoading =
    isDownloadLinksLoading || isLoading || isPublicSubscriptionGroupsLoading || isSubscriberContactMethodsLoading;

  return (
    <>
      <AddSubscriberContactMethodModal
        open={isAddContactMethodModalOpen}
        onClose={() => setIsAddContactMethodModalOpen(false)}
        onSubmit={async ({ identifier, type }) => {
          await sendAuthOTP({
            identifier,
            type,
            returnUrl: buildReturnUrl(type, identifier),
          });
        }}
        isLoading={isAuthOTPSendLoading}
        submitTitle='Add'
        showSignInLink={false}
      />
      <VerifyOTPModal
        data={otpModalData}
        onClose={() => setOTPModalData(null)}
        onChangeIdentifier={() => {
          setOTPModalData(null);
          setIsAddContactMethodModalOpen(true);
        }}
        verifyOTPMutation={{
          isLoading: isAuthIssueAnonymousTokenLoading,
          mutate: async ({ identifier, type, otp }) => {
            await issueAnonymousTokenByOTP({
              identifier,
              type,
              otp,
            });
          },
        }}
      />
      <Loader
        className={classNames({
          "tw-hidden": !isPageLoading,
        })}
        active
      >
        Loading...
      </Loader>
      <div
        className={classNames("tw-mb-24", {
          "tw-hidden": isPageLoading,
        })}
      >
        <NotifyPublicTopNavigation />
        <Container>
          <Header as='h1' className='tw-mb-3 tw-mt-6 md:tw-my-10'>
            Manage Your Notifications
          </Header>
          <div className='tw-flex tw-flex-col tw-gap-3 lg:tw-flex-row'>
            {!isLoggedIn && <ManageEmailOrPhoneNumberCard />}
            <Segment className='tw-h-42 tw-m-0 tw-flex tw-w-full tw-justify-between tw-gap-3 tw-space-y-1 tw-p-5'>
              <div>
                <Header as='h2' className='tw-m-0 tw-text-lg'>
                  Download the App
                </Header>
                <p>Push notifications will be sent directly to your phone.</p>
                <div className='tw-flex tw-gap-2'>
                  <a href={downloadLinks?.appleLink ?? ""}>
                    <img
                      src='https://assets.govoutreach.com/static/ios-appstore-badge-black-2025.png'
                      alt='App Store'
                      className='tw-h-8'
                    />
                  </a>
                  <a href={downloadLinks?.googleLink ?? ""}>
                    <img
                      src='https://assets.govoutreach.com/static/google-play-badge-2025.png'
                      alt='Google Play'
                      className='tw-h-8'
                    />
                  </a>
                </div>
              </div>
              <div className='tw-relative tw-hidden xs:tw-block'>
                <Loader active={delayedIsDevicePreviewLoading} size='small' />
                <div
                  className={classNames(
                    "tw-transition-opacity tw-duration-100",
                    delayedIsDevicePreviewLoading ? "tw-opacity-0" : "tw-opacity-100",
                  )}
                >
                  <img
                    src='/assets/notify/device-frames.png'
                    alt='App Examples'
                    className='tw-relative tw-z-30 tw-max-w-[7rem]'
                  />
                  <div className='tw-absolute tw-right-[-182.5px] tw-top-[-488px] tw-z-20 tw-h-[1100px] tw-w-[500px] tw-scale-[10.7%] tw-select-none tw-overflow-hidden tw-rounded-[50px] tw-bg-white'>
                    <DashboardPreview />
                  </div>
                  <div className='tw-absolute tw-right-[-229px] tw-top-[-487.5px] tw-z-10 tw-h-[1100px] tw-w-[500px] tw-scale-[8%] tw-select-none tw-overflow-hidden tw-rounded-[50px] tw-bg-white'>
                    <NotificationPreview />
                  </div>
                </div>
              </div>
            </Segment>
            {isConfigured && (
              <Segment className='tw-h-42 tw-m-0 tw-flex tw-w-full tw-justify-between tw-gap-3 tw-space-y-1 tw-p-5'>
                <div className='tw-space-y-1'>
                  <Header as='h2' className='tw-m-0 tw-text-lg'>
                    <ParsedPhoneNumber value={publicNotifySettings?.sitePhoneNumber} />
                  </Header>
                  <p className='tw-m-0'>
                    Text{" "}
                    <b>
                      {'"'}JOIN{'"'}
                    </b>{" "}
                    or <b>Call</b> for updates
                  </p>
                  <div className='tw-flex tw-gap-2'>
                    <Button
                      size='tiny'
                      secondary
                      className='tw-m-0'
                      as='a'
                      href={`sms:${publicNotifySettings?.sitePhoneNumber}?body=JOIN`}
                    >
                      Text {'"'}JOIN{'"'}
                    </Button>
                    <Button
                      size='tiny'
                      secondary
                      className='tw-m-0'
                      as='a'
                      href={`tel:${publicNotifySettings?.sitePhoneNumber}`}
                    >
                      Call
                    </Button>
                  </div>
                </div>
                <div className='tw-hidden xs:tw-block'>
                  <img src='/assets/notify/text-preview.png' alt='SMS Preview' className='tw-max-w-[11rem]' />
                </div>
              </Segment>
            )}
          </div>
          {isLoggedIn && (
            <>
              <Header as='h2' className='tw-mb-3 tw-mt-6 md:tw-mt-10'>
                Contact Methods
              </Header>
              <SubscriberContactMethodTable />
            </>
          )}
          <Header as='h2' className='tw-mb-3 tw-mt-6 md:tw-mt-10'>
            Subscription Groups
          </Header>
          {isLoggedIn && <LoggedInSubscriptionGroupsTable />}
          {!isLoggedIn && isLoggedInAnonymously && <AnonymousSubscriptionGroupsTable />}
          {!isLoggedIn && !isLoggedInAnonymously && <PublicSubscriptionGroupsTable />}
        </Container>
      </div>
    </>
  );
}

function useQueryHasData(queryKey: any) {
  const queryCache = useQueryCache();
  const query = queryCache.getQuery(queryKey);

  const [hasData, setHasData] = useState(!!query?.state.data);

  useEffect(() => {
    const unsubscribe = queryCache.subscribe((_, query) => {
      const keyMatches = JSON.stringify(query?.queryKey) === JSON.stringify(queryKey);
      if (keyMatches && !!query?.state.data) setHasData(true);
    });

    return unsubscribe;
  }, [queryKey, queryCache]);

  return hasData;
}
